import { Input } from "./Input";
import { useState } from "react";
import { useFormikContext } from "formik";

const VariableDropdown = ({
  variables,
  onSelect,
}: {
  variables: Record<string, string[]>;
  onSelect: (variable: string) => void;
}) => {
  return (
    <div className="absolute z-50 bg-white dark:bg-zinc-800 border dark:border-white/10 shadow-lg rounded-md p-2 w-64 pb-1 mt-2">
      {Object.entries(variables).map(([category, vars]) => (
        <div key={category} className="mb-2">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-white/40 mb-1">
            {category}
          </h4>
          {category === "Form Variables" ? (
            <div className="dark:text-white text-xs ml-1">Coming soon</div>
          ) : (
            vars.map((variable) => (
              <button
                key={variable}
                onClick={() => onSelect(variable)}
                className="block w-full text-left text-sm text-gray-700 dark:text-white px-2 py-1 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded"
              >
                {variable}
              </button>
            ))
          )}
        </div>
      ))}
    </div>
  );
};

interface Props {
  type: string;
  name: string;
  required: boolean;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<any>) => void;
  variables: Record<string, string[]>; // Pass available variables as props
}

export const FormSettingsInput = ({
  type,
  name,
  required,
  value,
  placeholder,
  disabled,
  onChange,
  variables,
}: Props) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const { setFieldValue, values } = useFormikContext<any>();

  const handleVariableSelect = (variable: string) => {
    if (cursorPosition !== null) {
      const currentValue = values[name] || "";
      const beforeCursor = currentValue.slice(0, cursorPosition - 2); // Remove `{{`
      const afterCursor = currentValue.slice(cursorPosition);
      const newValue = `${beforeCursor}{{${variable}}}${afterCursor}`;

      // Update the Formik field value
      setFieldValue(name, newValue);

      setShowDropdown(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    if (
      cursorPos !== null &&
      currentValue.slice(cursorPos - 2, cursorPos) === "{{"
    ) {
      setShowDropdown(true);
      setCursorPosition(cursorPos);
    } else {
      setShowDropdown(false);
    }

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className="relative">
      <Input
        type={type}
        name={name}
        required={required}
        value={values[name]}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleInputChange}
      />
      {showDropdown && (
        <VariableDropdown
          variables={variables}
          onSelect={handleVariableSelect}
        />
      )}
    </div>
  );
};
